import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Dialog, DialogContent, IconButton } from '@mui/material';
import { ContainedMediaOverlay } from '../components/liveView/ContainedMediaOverlay';
import { scaleNormalizedPolygonToImageBox } from './overviewV2VisualGeometry';

const colors = {
  ink: '#0f172a',
  ink2: '#4a5768',
  ink3: '#7d8798',
  line: '#e3e8ef',
  blue: '#1d4ed8',
  green: '#12744a',
  greenSoft: '#eef9f2',
};

const OVERLAY_PALETTE = [
  { fill: 'rgba(29, 78, 216, 0.16)', stroke: 'rgba(29, 78, 216, 0.94)' },
  { fill: 'rgba(15, 118, 110, 0.16)', stroke: 'rgba(15, 118, 110, 0.94)' },
  { fill: 'rgba(124, 58, 237, 0.16)', stroke: 'rgba(124, 58, 237, 0.94)' },
];

export type OverviewVisualTruthState = 'A' | 'B' | 'C' | 'D';

export type OverviewAreaGeometry = {
  zoneId: string | null;
  label: string | null;
  polygon: number[][] | null;
  sourceLabel?: string | null;
  deployed?: boolean;
  conditionId?: string | null;
  measurementScopeKind?: 'ENTIRE_CAMERA_VIEW' | 'ZONE_POLYGON';
};

export type OverviewAreaCameraPanelModel = {
  areaTitle: string;
  cameraLabel: string;
  cameraId: string | null;
  deviceId: string | null;
  zoneLabel: string;
  zoneId: string | null;
  snapshotUrl: string | null;
  snapshotPending?: boolean;
  geometry: OverviewAreaGeometry | null;
  overlays?: OverviewAreaGeometry[];
  cameraPath: string | null;
  livePath: string | null;
  zonePath: string | null;
  showFixtureProvenance?: boolean;
  areaName?: string | null;
  mountLabel?: string | null;
};

type OverviewAreaCameraPanelProps = {
  model: OverviewAreaCameraPanelModel;
  onNavigate: (path: string) => void;
  variant?: 'full' | 'compact';
  actions?: 'default' | 'simplified';
  viewerOpen?: boolean;
  focusZoneId?: string | null;
  onViewerOpenChange?: (open: boolean) => void;
  onSnapshotOpen?: () => void;
};

export function overviewVisualTruthState(snapshotUrl: string | null, geometry: OverviewAreaGeometry | null): OverviewVisualTruthState {
  const hasSnapshot = Boolean(snapshotUrl);
  const hasGeometry = Boolean(geometry?.polygon && geometry.polygon.length >= 3);
  if (hasSnapshot && hasGeometry) return 'A';
  if (hasSnapshot) return 'B';
  if (hasGeometry) return 'C';
  return 'D';
}

function provenOverlays(model: OverviewAreaCameraPanelModel): OverviewAreaGeometry[] {
  const listed = Array.isArray(model.overlays) ? model.overlays : [];
  const fromList = listed.filter((item) => item?.polygon && item.polygon.length >= 3 && item.deployed !== false);
  if (fromList.length) return fromList;
  if (model.geometry?.polygon && model.geometry.polygon.length >= 3 && model.geometry.deployed !== false) {
    return [model.geometry];
  }
  return [];
}

function OverviewPanelLink({ href, children, onNavigate }: { href: string; children: React.ReactNode; onNavigate: (path: string) => void }) {
  return (
    <Box
      component="a"
      href={href}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        onNavigate(href);
      }}
      sx={{ color: colors.blue, textDecoration: 'none', fontWeight: 700, fontSize: 9.5 }}
    >
      {children}
    </Box>
  );
}

function customerStatusLine(state: OverviewVisualTruthState): string | null {
  if (state === 'A') return null;
  if (state === 'B') return 'Zone outline unavailable';
  if (state === 'C') return 'Camera snapshot unavailable';
  return 'Camera snapshot and zone outline unavailable';
}

function ZoneOverlays({
  overlays,
  box,
  focusZoneId,
  testId,
}: {
  overlays: OverviewAreaGeometry[];
  box: { width: number; height: number };
  focusZoneId?: string | null;
  testId?: string;
}) {
  const drawn = overlays
    .map((geometry, index) => {
      const scaled = scaleNormalizedPolygonToImageBox(geometry.polygon, box);
      if (!scaled) return null;
      return { geometry, scaled, index };
    })
    .filter(Boolean) as Array<{ geometry: OverviewAreaGeometry; scaled: number[][]; index: number }>;
  if (!drawn.length) return null;
  return (
    <svg
      viewBox={`0 0 ${box.width} ${box.height}`}
      preserveAspectRatio="xMidYMid meet"
      data-testid={testId || 'overview-v2-zone-overlay'}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      {drawn.map(({ geometry, scaled, index }) => {
        const focused = Boolean(focusZoneId && geometry.zoneId && geometry.zoneId === focusZoneId);
        const palette = OVERLAY_PALETTE[index % OVERLAY_PALETTE.length];
        return (
          <polygon
            key={`${geometry.zoneId || geometry.label || 'zone'}-${index}`}
            data-testid="overview-v2-zone-overlay-polygon"
            data-zone-id={geometry.zoneId || ''}
            data-focused={focused ? 'true' : 'false'}
            points={scaled.map(([x, y]) => `${x},${y}`).join(' ')}
            fill={palette.fill}
            stroke={focused ? 'rgba(15, 23, 42, 0.96)' : palette.stroke}
            strokeWidth={Math.max(focused ? 2.4 : 1.5, Math.min(box.width, box.height) * (focused ? 0.012 : 0.008))}
          />
        );
      })}
    </svg>
  );
}

function cameraViewLabel(model: OverviewAreaCameraPanelModel): string {
  const place = [model.areaTitle, model.cameraLabel].filter(Boolean).join(' – ');
  return `Open camera view for ${place || 'this camera'}`;
}

function areaLine(model: OverviewAreaCameraPanelModel): string | null {
  const area = String(model.areaName || '').trim();
  return area || null;
}

export function OverviewAreaCameraPanel({
  model,
  onNavigate,
  variant = 'full',
  actions = 'default',
  viewerOpen,
  focusZoneId = null,
  onViewerOpenChange,
  onSnapshotOpen,
}: OverviewAreaCameraPanelProps) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [imageBox, setImageBox] = useState({ width: 0, height: 0 });
  const [imageFailed, setImageFailed] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const [viewerBox, setViewerBox] = useState({ width: 0, height: 0 });
  const overlays = provenOverlays(model);
  const primaryGeometry = overlays[0] || null;
  const showSnapshot = Boolean(model.snapshotUrl) && !imageFailed;
  const truthState = overviewVisualTruthState(showSnapshot ? model.snapshotUrl : null, primaryGeometry);
  const drawableGeometry = truthState === 'A' ? primaryGeometry : null;
  const snapshotPending = Boolean(model.snapshotPending) && !showSnapshot && !imageFailed;
  const open = viewerOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (viewerOpen === undefined) setInternalOpen(next);
    onViewerOpenChange?.(next);
  };

  useEffect(() => {
    setImageFailed(false);
  }, [model.snapshotUrl]);

  useEffect(() => {
    if (!open || !showSnapshot) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (viewerOpen === undefined) setInternalOpen(false);
        onViewerOpenChange?.(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, showSnapshot, viewerOpen, onViewerOpenChange]);

  useLayoutEffect(() => {
    const node = frameRef.current;
    if (!node || !drawableGeometry) {
      setImageBox({ width: 0, height: 0 });
      return undefined;
    }
    const measure = () => {
      const width = node.clientWidth;
      const height = node.clientHeight;
      setImageBox((current) => (
        current.width === width && current.height === height ? current : { width, height }
      ));
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [drawableGeometry, model.snapshotUrl]);
  const statusLine = snapshotPending ? null : customerStatusLine(truthState);
  const links = actions === 'simplified'
    ? []
    : ([
      model.cameraPath ? { href: model.cameraPath, label: 'Open camera' } : null,
      model.livePath ? { href: model.livePath, label: 'Live view' } : null,
    ].filter(Boolean) as Array<{ href: string; label: string }>);

  const compact = variant === 'compact';
  const headerTitle = model.areaTitle || model.cameraLabel;
  const focusedOverlay = overlays.find((item) => item.zoneId && item.zoneId === focusZoneId) || null;
  const focusedMeasurementLabel = focusedOverlay?.measurementScopeKind === 'ZONE_POLYGON'
    ? `Measurement scope: ${focusedOverlay.label || model.zoneLabel}`
    : null;

  const openViewer = () => {
    if (!showSnapshot) return;
    if (onSnapshotOpen) {
      onSnapshotOpen();
      return;
    }
    setOpen(true);
  };

  return (
    <Box
      data-testid="overview-v2-camera-zone-panel"
      data-truth-state={truthState}
      data-variant={variant}
      data-snapshot-pending={snapshotPending ? 'true' : 'false'}
      data-camera-id={model.cameraId || ''}
      data-device-id={model.deviceId || ''}
      sx={compact ? {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        minWidth: 0,
      } : {
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '288px 1fr' },
        gap: '12px',
        p: '10px 12px',
        bgcolor: '#fbfcfe',
        borderBottom: `1px solid ${colors.line}`,
      }}
    >
      <Box
        ref={frameRef}
        data-testid="overview-v2-snapshot-frame"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '8px',
          border: `1px solid ${colors.line}`,
          bgcolor: '#e9eef6',
          flex: compact ? '0 0 112px' : undefined,
          flexShrink: compact ? 0 : undefined,
          width: compact ? 112 : undefined,
          minWidth: compact ? 112 : undefined,
          minHeight: compact ? 64 : { xs: 148, md: 162 },
          height: compact ? 64 : undefined,
          display: compact ? 'flex' : 'grid',
          alignItems: 'center',
          justifyContent: 'center',
          placeItems: compact ? undefined : 'center',
        }}
      >
        {showSnapshot ? (
          <Box
            component="button"
            type="button"
            ref={triggerRef}
            data-testid="overview-v2-snapshot-open"
            aria-label={cameraViewLabel(model)}
            onClick={openViewer}
            onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openViewer();
              }
            }}
            sx={{
              all: 'unset',
              display: 'block',
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              boxSizing: 'border-box',
              '&:focus-visible': {
                outline: `2px solid ${colors.blue}`,
                outlineOffset: '-2px',
              },
            }}
          >
            <Box
              component="img"
              src={model.snapshotUrl || undefined}
              alt={`${model.areaTitle} camera snapshot`}
              data-testid="overview-v2-snapshot-image"
              onError={() => setImageFailed(true)}
              sx={{
                width: '100%',
                height: compact ? '100%' : 'auto',
                minHeight: compact ? 64 : undefined,
                maxHeight: compact ? 'none' : '100%',
                display: 'block',
                objectFit: compact ? 'cover' : 'contain',
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'none',
              }}
            />
          </Box>
        ) : (
          <Box
            component="span"
            data-testid="overview-v2-snapshot-placeholder"
            sx={{ px: '10px', textAlign: 'center', fontSize: 11, lineHeight: 1.3, color: colors.ink3 }}
          >
            {snapshotPending ? 'Loading snapshot' : 'Snapshot unavailable'}
          </Box>
        )}
        {drawableGeometry ? <ZoneOverlays overlays={overlays} box={imageBox} /> : null}
      </Box>

      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '8px' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', mb: '6px' }}>
            <Box component="b" sx={{ fontSize: compact ? 9.5 : 10.5, color: colors.ink }}>
              Camera view
            </Box>
            {truthState === 'A' ? (
              <Box
                component="span"
                data-testid="overview-v2-visual-truth-A"
                sx={{
                  bgcolor: colors.greenSoft,
                  color: colors.green,
                  borderRadius: 999,
                  px: '7px',
                  py: '3px',
                  fontSize: 8.5,
                  fontWeight: 760,
                  whiteSpace: 'nowrap',
                }}
              >
                Zone shown
              </Box>
            ) : null}
          </Box>
          <Box data-testid="overview-v2-camera-zone-names" sx={{ fontSize: 12, color: colors.ink2, lineHeight: 1.5 }}>
            <Box>
              Camera: <Box component="b" sx={{ color: colors.ink }}>{model.cameraLabel}</Box>
            </Box>
            {model.mountLabel ? (
              <Box data-testid="overview-v2-camera-mount">
                Mount: <Box component="b" sx={{ color: colors.ink }}>{model.mountLabel}</Box>
              </Box>
            ) : null}
            {model.showFixtureProvenance && (primaryGeometry?.sourceLabel || model.geometry?.sourceLabel) ? (
              <Box data-testid="overview-v2-zone-geometry-source" sx={{ color: colors.ink3, fontSize: 10 }}>
                Geometry source: {primaryGeometry?.sourceLabel || model.geometry?.sourceLabel}
              </Box>
            ) : null}
          </Box>
          {statusLine ? (
            <Box data-testid="overview-v2-visual-status" sx={{ mt: '6px', fontSize: 10.5, color: colors.ink3 }}>
              {statusLine}
            </Box>
          ) : (
            <Box data-testid="overview-v2-visual-status" sx={{ display: 'none' }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {links.map((link) => (
            <OverviewPanelLink key={link.href} href={link.href} onNavigate={onNavigate}>
              {link.label}
            </OverviewPanelLink>
          ))}
        </Box>
      </Box>

      <Dialog
        open={open && showSnapshot}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="overview-v2-camera-view-title"
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', p: '12px 16px 8px' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box
              component="h2"
              id="overview-v2-camera-view-title"
              data-testid="overview-v2-camera-view-title"
              sx={{ m: 0, fontSize: 16, fontWeight: 750, color: colors.ink }}
            >
              {headerTitle}
            </Box>
            {areaLine(model) ? (
              <Box data-testid="overview-v2-camera-view-area" sx={{ mt: '2px', fontSize: 12, color: colors.ink2 }}>
                Area: {areaLine(model)}
              </Box>
            ) : null}
          </Box>
          <IconButton
            aria-label="Close camera view"
            data-testid="overview-v2-camera-view-close"
            onClick={() => setOpen(false)}
            size="small"
          >
            ×
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0 }}>
          <Box
            data-testid="overview-v2-camera-view"
            data-camera-id={model.cameraId || ''}
            data-focus-zone-id={focusZoneId || ''}
            sx={{
              position: 'relative',
              width: '100%',
              minHeight: { xs: 220, md: 360 },
              bgcolor: '#0f172a',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <ContainedMediaOverlay
              src={model.snapshotUrl || ''}
              alt={`${model.areaTitle} camera snapshot`}
              overlay={overlays.length ? (
                <ViewerOverlayHost
                  overlays={overlays}
                  focusZoneId={focusZoneId}
                  onBox={setViewerBox}
                  box={viewerBox}
                />
              ) : null}
            />
          </Box>
          {overlays.length ? (
            <Box data-testid="overview-v2-camera-view-legend" sx={{ mt: '10px' }}>
              <Box sx={{ fontSize: 10, fontWeight: 750, letterSpacing: '.4px', textTransform: 'uppercase', color: colors.ink3 }}>
                Configured zones in this camera view
              </Box>
              {overlays.map((overlay, index) => (
                <Box
                  key={`${overlay.zoneId || overlay.label || 'zone'}-${index}`}
                  data-testid="overview-v2-camera-view-legend-item"
                  data-zone-id={overlay.zoneId || ''}
                  data-focused={focusZoneId && overlay.zoneId === focusZoneId ? 'true' : 'false'}
                  sx={{ mt: '4px', fontSize: 12, color: colors.ink }}
                >
                  {overlay.label || overlay.zoneId}
                  <Box component="span" sx={{ ml: '6px', color: colors.ink3, fontSize: 11 }}>deployed</Box>
                </Box>
              ))}
              {focusedMeasurementLabel ? (
                <Box data-testid="overview-v2-camera-view-measurement-scope" sx={{ mt: '8px', fontSize: 12, fontWeight: 700, color: colors.ink }}>
                  {focusedMeasurementLabel}
                </Box>
              ) : null}
            </Box>
          ) : (
            <Box data-testid="overview-v2-camera-view-status" sx={{ mt: '10px', fontSize: 12, color: colors.ink3 }}>
              Zone outline unavailable
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function ViewerOverlayHost({
  overlays,
  focusZoneId,
  box,
  onBox,
}: {
  overlays: OverviewAreaGeometry[];
  focusZoneId?: string | null;
  box: { width: number; height: number };
  onBox: (box: { width: number; height: number }) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => {
      const width = node.clientWidth;
      const height = node.clientHeight;
      onBox({ width, height });
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [onBox]);
  return (
    <Box ref={ref} sx={{ position: 'absolute', inset: 0 }}>
      <ZoneOverlays
        overlays={overlays}
        box={box}
        focusZoneId={focusZoneId}
        testId="overview-v2-camera-view-overlay"
      />
    </Box>
  );
}

export default OverviewAreaCameraPanel;
